import React, { useState, useEffect } from 'react';
import './AdminPage.css';

const AdminPage = ({ user }) => {
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingApprover, setEditingApprover] = useState(null);
  const [formData, setFormData] = useState({ name: '', emailAddress: '', emailNotification: false });

  useEffect(() => {
    fetchApprovers();
  }, []);

  const fetchApprovers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/approvers/list');
      const data = await response.json();
      // Ensure data is an array
      setApprovers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching approvers:', error);
      setMessage('Error loading approvers');
      setApprovers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({ name: '', emailAddress: '', emailNotification: false });
    setEditingApprover(null);
    setShowAddModal(true);
  };

  const handleEdit = (approver) => {
    setFormData({
      name: approver.name,
      emailAddress: approver.email_address,
      emailNotification: approver.email_notification || false
    });
    setEditingApprover(approver);
    setShowAddModal(true);
  };

  const handleDelete = async (approverId) => {
    if (!window.confirm('Are you sure you want to delete this approver?')) {
      return;
    }

    try {
      const response = await fetch(`/api/approvers/delete/${approverId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Approver deleted successfully');
        fetchApprovers();
      } else {
        setMessage('Error deleting approver: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting approver:', error);
      setMessage('Error deleting approver');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      const url = editingApprover
        ? `/api/approvers/update/${editingApprover.approver_id}`
        : '/api/approvers/add';

      const method = editingApprover ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          [editingApprover ? 'updatedBy' : 'createdBy']: user.email
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(`Approver ${editingApprover ? 'updated' : 'added'} successfully`);
        setShowAddModal(false);
        fetchApprovers();
      } else {
        setMessage('Error: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving approver:', error);
      setMessage('Error saving approver');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Administrator Panel</h2>
        <p>Manage approvers and system settings</p>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="admin-section">
        <div className="section-header">
          <h3>Approver Management</h3>
          <button onClick={handleAdd} className="btn-add">
            + Add Approver
          </button>
        </div>

        <div className="approvers-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email Address</th>
                <th>Email Notification</th>
                <th>Created By</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {approvers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    No approvers found
                  </td>
                </tr>
              ) : (
                approvers.map(approver => (
                  <tr key={approver.approver_id}>
                    <td>{approver.name}</td>
                    <td>{approver.email_address}</td>
                    <td>
                      <span className={`notification-badge ${approver.email_notification ? 'enabled' : 'disabled'}`}>
                        {approver.email_notification ? '✓ Enabled' : '✗ Disabled'}
                      </span>
                    </td>
                    <td>{approver.created_by}</td>
                    <td>{new Date(approver.creation_date).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEdit(approver)}
                          className="btn-edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(approver.approver_id)}
                          className="btn-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingApprover ? 'Edit Approver' : 'Add New Approver'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter full name"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={formData.emailAddress}
                  onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.emailNotification}
                    onChange={(e) => setFormData({ ...formData, emailNotification: e.target.checked })}
                  />
                  <span>Enable Email Notifications</span>
                </label>
                <p className="helper-text">
                  When enabled, this approver will receive email notifications when new requests are submitted.
                </p>
              </div>

              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingApprover ? 'Update' : 'Add'} Approver
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;

